import { useCallback, useEffect, useState } from 'react';
import { Alert, Modal, Skeleton } from '@heroui/react';
import { sendDataAPI } from '@lib/sendDataAPI';
import { useSession } from '@context/sessionProvider';
import { ActivePlan, PlanData, PlanInfo } from '@/types';
import FreePlanCard from '@components/plans/free-plan-card';
import PaidPlanCard from '@components/plans/paid-plan-card';

const PLAN_SKELETON_KEYS = ['plan-skeleton-primary', 'plan-skeleton-secondary'];

interface SubscriptionInfoProps {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	isFree: boolean;
}

const PlansModal = (props: SubscriptionInfoProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [plans, setPlans] = useState<PlanInfo[]>([]);
	const [activePlan, setActivePlan] = useState<ActivePlan>();

	const sessionData = useSession();

	const load = useCallback(async () => {
		setIsLoading(true);

		let response: PlanData = await sendDataAPI({ jwtToken: sessionData.context }, '/api/plans/get', 'GET');
		response = await response.json();

		if (response.data) {
			setPlans(response.data.plans);
			setActivePlan(response.data.activePlan);
		}

		setIsLoading(false);
	}, [sessionData.context]);

	useEffect(() => {
		load().catch(console.error);
	}, [sessionData.context, load]);

	const getAllPlansMarkup = () => (
		<div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
			{isLoading
				? PLAN_SKELETON_KEYS.map((key) => (
						<Skeleton key={key} className='rounded-lg'>
							<div className='h-[450px] bg-gray-300 rounded-lg' />
						</Skeleton>
					))
				: plans.map((plan) => renderPlanBasedOnType(plan))}
		</div>
	);

	const renderPlanBasedOnType = (plan: PlanInfo) => {
		if (plan.name === 'Free') {
			return <FreePlanCard key={plan.id} plan={plan} isActive={props.isFree} />;
		} else {
			return (
				<PaidPlanCard
					key={plan.id}
					plan={plan}
					isActive={!props.isFree}
					activePlan={activePlan}
					load={load}
					setIsLoading={setIsLoading}
				/>
			);
		}
	};

	const timestampToString = (timestamp: number) => {
		const date = new Date(timestamp * 1000);

		const day = date.getDate().toString();
		const month = date.toLocaleString('en-GB', { month: 'long' });
		const year = date.getFullYear();
		const time = date.toLocaleTimeString();

		return `${day} ${month} ${year} at ${time}`;
	};

	return (
		<Modal>
			<Modal.Backdrop isOpen={props.isOpen} onOpenChange={props.onOpenChange}>
				<Modal.Container size='lg'>
					<Modal.Dialog>
						<Modal.Header className='flex flex-col gap-1'>
							<Modal.Heading>Plans</Modal.Heading>
						</Modal.Header>
						<Modal.Body className='pb-6'>
							{activePlan?.expired_at !== null && (
								<Alert status='warning' className='mb-3'>
									<Alert.Content>
										<Alert.Title>
											Your app subscription will be suspended on{' '}
											<b>{timestampToString(Number(activePlan?.expired_at))}</b>, but you can reactivate it anytime
											before it expires.
										</Alert.Title>
									</Alert.Content>
								</Alert>
							)}
							{getAllPlansMarkup()}
						</Modal.Body>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
};

export default PlansModal;
